import { createResource, createSignal, For, Show } from "solid-js";
import type { AdapterInfo, CredentialField } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t, tOr } from "../i18n.ts";
import { formatDateTime } from "../format.ts";
import { ErrorBox } from "./ErrorBox.tsx";
import { operator, useAction } from "./ui.tsx";

/**
 * Credentials of a connection whose account is authorized by signing in at the vendor (e.g. Lavva): the settings
 * the sign-in needs (environment), the sign-in itself (the browser goes to the vendor and comes back), and what to
 * read from the signed-in account (installations). The vendor password never reaches the platform.
 */
export function AccountCredentials(props: {
  connectionId: string;
  adapter: AdapterInfo;
}) {
  const [state, { refetch }] = createResource(() =>
    api.connections.credentials(props.connectionId),
  );
  const [account] = createResource(() =>
    api.connections.account(props.connectionId),
  );
  const [edited, setEdited] = createSignal<Record<string, string>>({});
  const [run, error, busy] = useAction();

  const plain = () => props.adapter.credentials.filter((f) => !f.fromAccount);
  const fromAccount = () =>
    props.adapter.credentials.filter((f) => f.fromAccount);
  const value = (f: CredentialField) =>
    edited()[f.name] ?? state()?.values[f.name] ?? f.default ?? "";
  const current = () =>
    Object.fromEntries(
      props.adapter.credentials
        .map((f) => [f.name, value(f)])
        .filter(([, v]) => v !== ""),
    );

  // The settings are saved first, so the sign-in uses the environment chosen here.
  const signIn = () =>
    void run(async () => {
      await api.connections.saveCredentials(props.connectionId, current());
      const { url } = await api.connections.signIn(props.connectionId);
      window.location.assign(url);
    });

  return (
    <div class="card-b stack">
      <div class="axes">
        <div class="axis">
          <span class="eyebrow">{t("account.title")}</span>
          <Show
            when={account()}
            fallback={<span class="hint">{t("common.loading")}</span>}
          >
            {(a) => (
              <Show
                when={a().signedIn}
                fallback={
                  <span class="chip warn">{t("account.notSignedIn")}</span>
                }
              >
                <span class="chip ok">
                  {t("account.signedInAs", { account: a().account ?? "" })}
                </span>
                <span class="hint">
                  {t("account.since", {
                    at: formatDateTime(a().signedInAt, locale()),
                  })}
                </span>
              </Show>
            )}
          </Show>
        </div>
      </div>
      <Show when={operator()}>
        <For each={plain()}>
          {(f) => (
            <div class="field">
              <label for={`acc-${f.name}`}>
                {tOr(`credential.${f.name}`, f.name)}
              </label>
              <Show
                when={f.options}
                fallback={
                  <input
                    id={`acc-${f.name}`}
                    value={value(f)}
                    onInput={(e) =>
                      setEdited({
                        ...edited(),
                        [f.name]: e.currentTarget.value,
                      })
                    }
                  />
                }
              >
                {(options) => (
                  <select
                    id={`acc-${f.name}`}
                    value={value(f)}
                    onChange={(e) =>
                      setEdited({
                        ...edited(),
                        [f.name]: e.currentTarget.value,
                      })
                    }
                  >
                    <For each={options()}>
                      {(o) => (
                        <option value={o}>
                          {tOr(`credentialOption.${f.name}.${o}`, o)}
                        </option>
                      )}
                    </For>
                  </select>
                )}
              </Show>
            </div>
          )}
        </For>
        <div class="row-acts">
          <button
            type="button"
            class="btn primary"
            disabled={busy()}
            onClick={signIn}
          >
            {account()?.signedIn
              ? t("account.signInAgain", { vendor: props.adapter.vendor })
              : t("account.signIn", { vendor: props.adapter.vendor })}
          </button>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t("account.signInHint", { vendor: props.adapter.vendor })}
        </p>
        <Show when={account()?.signedIn}>
          <For each={fromAccount()}>
            {(f) => (
              <AccountChoice
                connectionId={props.connectionId}
                field={f}
                selected={value(f)}
                onSave={(v) =>
                  run(async () => {
                    await api.connections.saveCredentials(props.connectionId, {
                      ...current(),
                      [f.name]: v,
                    });
                    setEdited({});
                    await refetch();
                  })
                }
              />
            )}
          </For>
        </Show>
      </Show>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
    </div>
  );
}

/** One or more values listed from the signed-in account, e.g. which installations to read. */
function AccountChoice(props: {
  connectionId: string;
  field: CredentialField;
  selected: string;
  onSave: (value: string) => Promise<boolean>;
}) {
  const [options] = createResource(() =>
    api.connections.credentialOptions(props.connectionId, props.field.name),
  );
  const [checked, setChecked] = createSignal<ReadonlySet<string>>(
    new Set(
      props.selected
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v !== ""),
    ),
  );
  const toggle = (v: string, on: boolean) => {
    const next = new Set(checked());
    if (on) {
      next.add(v);
    } else {
      next.delete(v);
    }
    setChecked(next);
  };

  return (
    <div class="field">
      <span class="label">
        {tOr(`credentialAccount.${props.field.name}`, props.field.name)}
      </span>
      <Show when={!options.error} fallback={<ErrorBox error={options.error} />}>
        <Show
          when={options()}
          fallback={<span class="hint">{t("common.loading")}</span>}
        >
          {(list) => (
            <Show
              when={list().length > 0}
              fallback={<p class="hint">{t("account.noOptions")}</p>}
            >
              <div class="stack" style={{ gap: "6px" }}>
                <For each={list()}>
                  {(o) => (
                    <label class="opt">
                      <input
                        type="checkbox"
                        checked={checked().has(o.value)}
                        onChange={(e) =>
                          toggle(o.value, e.currentTarget.checked)
                        }
                      />
                      <span>
                        <b>{o.label}</b>
                        <small class="mono">{o.value}</small>
                      </span>
                    </label>
                  )}
                </For>
              </div>
              <div class="row-acts">
                <button
                  type="button"
                  class="btn primary"
                  disabled={checked().size === 0}
                  onClick={() => void props.onSave([...checked()].join(","))}
                >
                  {t("account.saveChoice")}
                </button>
              </div>
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}
